/* eslint-disable import/no-cycle -- pre-existing store-wiring cycle (errorLogger ← providers/store ← firebaseLogout), not introduced here; the same store wiring forces the matching disable in providers/store.js */
import { isRejectedWithValue } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import { handleFirebaseLogout } from 'providers/firebaseLogout'
import { IMPERSONATION_KEY } from 'providers/ImpersonationProvider'
import { ERROR_EXCEPTIONS } from 'settings/error'
import { captureError, getErrorMessage } from 'utils/error'
import { get } from 'utils/lodash'

import type { Middleware } from 'redux'

const { INVALID_USER_EXCEPTION, PERMISSION_DENIED } = ERROR_EXCEPTIONS

// localStorage can throw when storage is blocked, or be absent outside a browser (SSR/tests). Treat
// any failure as "not impersonating" so this middleware never breaks while handling a rejected action.
const isImpersonationSessionActive = () => {
  try {
    return Boolean(localStorage.getItem(IMPERSONATION_KEY))
  } catch {
    return false
  }
}

type RejectedAction = { payload?: { sessionExpired?: boolean } }

export const rtkQueryErrorLogger: Middleware = () => next => action => {
  if (isRejectedWithValue(action)) {
    const exceptionType = get(action, 'payload.data.exceptionType')

    // Only a 401 baseQueryWithReauth could not clear with a freshly minted token gets here.
    if ((action as RejectedAction)?.payload?.sessionExpired) {
      captureError('Signed out: a 401 survived a token refresh', { endpoint: get(action, 'meta.arg.endpointName', 'unknown') })
      handleFirebaseLogout()
    }

    if (exceptionType === INVALID_USER_EXCEPTION) {
      handleFirebaseLogout()
    }

    // Surface permission-denied feedback only during an impersonation session — the case this was
    // added for (a write blocked in a view-only session). For normal users a PERMISSION_DENIED is
    // typically a pre-existing background read 403 (e.g. a settings call on a page the user can't
    // fully access) that components already handle; toasting those globally surfaced raw internal
    // messages on every account view (CS-2900). toastId de-dupes against any local handleError.
    if (exceptionType === PERMISSION_DENIED && isImpersonationSessionActive()) {
      const message = getErrorMessage(action.payload)
      if (message) {
        // eslint-disable-next-line no-restricted-syntax -- deliberate middleware-level toast; the rule targets call sites
        toast.error(message, { toastId: message })
      }
    }
  }

  return next(action)
}
